import React, { useEffect, useRef, useState } from "react";
import { FiX, FiDownload, FiTrash2, FiLoader, FiFileText, FiInfo, FiCopy, FiCheck, FiClipboard, FiUploadCloud, FiGlobe } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds, formatSize } from "./audioShared";

const ACCEPT = ".pdf,.docx,.pptx,.odt,.html,.htm,.txt,.md,.csv";
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_CHARS = 200000;
const SHOWN_AT_FIRST = 150;

// Used until /core/translation/languages/ answers (and if it never does).
const FALLBACK_LANGUAGES = [
  ["en", "English"], ["ur", "Urdu"], ["hi", "Hindi"], ["pa", "Punjabi"], ["ar", "Arabic"], ["fa", "Persian"], ["tr", "Turkish"],
  ["bn", "Bengali"], ["fr", "French"], ["de", "German"], ["es", "Spanish"], ["pt", "Portuguese"], ["it", "Italian"], ["ru", "Russian"],
  ["zh-CN", "Chinese"], ["ja", "Japanese"], ["ko", "Korean"], ["id", "Indonesian"],
].map(([key, label]) => ({ key, label, rtl: ["ur", "ar", "fa"].includes(key) }));
const selectClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const pillClass = "px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors";

function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}

function CopyButton({ getText, label }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => { if (await copyText(getText())) { setDone(true); setTimeout(() => setDone(false), 1800); } }}
      className={pillClass}
    >
      {done ? <><FiCheck className="text-emerald-400" /> Copied</> : <><FiCopy /> {label}</>}
    </button>
  );
}

// AI Document Translation: a document (or pasted text) in, the same document in another language out.
// Word and PowerPoint files keep their layout; the work runs on the server and this page polls for the result.
export default function DocumentTranslatorTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("translation");
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [source, setSource] = useState("file");   // "file" | "text"
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [target, setTarget] = useState("ur");
  const [from, setFrom] = useState("auto");
  const [glossary, setGlossary] = useState("");
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [layout, setLayout] = useState("both");   // "both" | "translation"
  const [showAll, setShowAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef(null);

  const hasInput = source === "file" ? !!file : text.trim().length >= 2;
  const canSubmit = hasInput && !submitting && !running && from !== target;

  useEffect(() => {
    let alive = true;
    apiRequest("/core/translation/languages/").then(({ ok, data }) => {
      if (alive && ok && Array.isArray(data?.languages) && data.languages.length) setLanguages(data.languages);
    });
    return () => { alive = false; };
  }, []);

  // Show the newest finished translation automatically.
  useEffect(() => {
    if (latest && latest.status === "completed" && latest.has_transcript) setViewId(latest.id);
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setShowAll(false);
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => {
      if (alive) setDetail(ok ? data : null);
    });
    return () => { alive = false; };
  }, [viewId, clientId]);

  const pickFile = (picked) => {
    setFieldError("");
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) return setFieldError(`This file is larger than ${MAX_FILE_BYTES / 1024 / 1024} MB.`);
    setFile(picked);
  };

  const submit = async () => {
    setError("");
    setFieldError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "translation");
    form.append("target", target);
    form.append("source", from);
    form.append("glossary", glossary);
    if (source === "file") form.append("original", file);
    else form.append("text", text);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.original || data?.text || data?.target) return setFieldError(data.original || data.text || data.target);
      return setError(errorMessage(data));
    }
    setViewId(null);
    add(data);
  };

  const extra = detail?.segments && !Array.isArray(detail.segments) ? detail.segments : {};
  const pairs = extra.pairs || [];
  const stats = extra.stats || {};
  const targetInfo = languages.find((l) => l.key === detail?.language);
  const rtl = !!targetInfo?.rtl;
  const baseName = (detail?.original_name || "translation").replace(/\.[^.]+$/, "");
  const fileExtension = extra.extension || ".docx";
  const visible = showAll ? pairs : pairs.slice(0, SHOWN_AT_FIRST);
  const languageName = (code) => languages.find((l) => l.key === code || l.key.split("-")[0] === (code || "").split("-")[0])?.label || code;

  const downloadDocument = async () => {
    setDownloading(true);
    const filename = `${baseName}-${detail.language}${fileExtension}`;
    try {
      const response = await fetch(detail.result_audio);
      if (!response.ok) throw new Error("download failed");
      saveBlob(filename, await response.blob());
    } catch {
      window.open(detail.result_audio, "_blank", "noopener");   // the browser downloads it itself
    }
    setDownloading(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Document Translation</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Translate a contract, report, letter or presentation into another language. Upload the document or paste the text, choose the
        language, and get the translation next to the original. Word and PowerPoint files keep their layout, fonts and pictures.
      </p>

      <div className="flex justify-center mb-6">
        <div className="inline-flex rounded-full bg-white/5 border border-navy-border p-1">
          {[["file", "Upload a document", FiUploadCloud], ["text", "Paste text", FiClipboard]].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => { setSource(key); setFieldError(""); }}
              className={`px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${source === key ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "text-gray-300 hover:text-white"}`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </div>

      {source === "file" ? (
        <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
          {!file ? (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-12 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
            >
              <FiFileText className="mx-auto text-accent-light" size={32} />
              <p className="mt-3 text-sm text-gray-200">Click to choose a document, or drop it here</p>
              <p className="text-xs text-gray-500 mt-1">Word (.docx), PowerPoint (.pptx), PDF, OpenDocument, HTML, TXT - up to 25 MB</p>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-xl bg-navy border border-navy-border p-4">
              <div className="flex items-center gap-3 min-w-0">
                <FiFileText className="text-accent-light shrink-0" size={22} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatSize(file.size)}</p>
                </div>
              </div>
              <button type="button" onClick={() => { setFile(null); setFieldError(""); }} aria-label="Remove document" className="text-gray-400 hover:text-red-400 shrink-0"><FiX size={18} /></button>
            </div>
          )}
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
          <p className="text-xs text-gray-500 mt-3">
            Word and PowerPoint files come back in the same format. A PDF comes back as a Word document of the translated text.
            Scanned PDFs (pictures of pages) have no text to read.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="translate-text" className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your text</label>
            <span className="text-xs text-gray-500">{text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters</span>
          </div>
          <textarea
            id="translate-text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Paste the text you want translated. Separate paragraphs with a blank line..."
            className="w-full min-h-[220px] px-5 py-4 rounded-xl bg-navy border border-navy-border text-white text-base leading-relaxed focus:outline-none focus:border-accent-light resize-y"
          />
        </div>
      )}
      {fieldError && <p className="text-sm text-amber-300 mt-3">{fieldError}</p>}

      <div className="rounded-2xl bg-white/5 border border-navy-border p-6 mt-6 grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="translate-from" className="text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3">Document language</label>
          <select id="translate-from" className={selectClass} value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="auto">Detect automatically</option>
            {languages.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="translate-to" className="text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3">Translate into</label>
          <select id="translate-to" className={selectClass} value={target} onChange={(e) => setTarget(e.target.value)}>
            {languages.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
          {from === target && <p className="text-xs text-amber-300 mt-2">Choose two different languages.</p>}
        </div>
        <div className="md:col-span-2">
          <label htmlFor="translate-glossary" className="text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3">
            Keep these words as they are <span className="normal-case tracking-normal text-gray-500 font-normal">(optional)</span>
          </label>
          <input
            id="translate-glossary"
            value={glossary}
            onChange={(e) => setGlossary(e.target.value)}
            placeholder="Brand, product or person names, separated by commas - e.g. Mraali Online, Evening Glow"
            className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light"
          />
          <p className="text-xs text-gray-500 mt-2">Web addresses and e-mail addresses are always kept exactly as written.</p>
        </div>
      </div>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Uploading...</> : running ? <><FiLoader className="animate-spin" /> Translating...</> : <><FiGlobe /> Translate</>}
      </button>
      {!canSubmit && !running && !submitting && from !== target && (
        <p className="text-xs text-gray-500 text-center mt-2">{source === "file" ? "Add a document first" : "Paste some text first"}</p>
      )}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div>
            <p className="font-semibold">
              Translating... {formatSeconds(elapsed)}
              {running.progress && <span className="text-gray-400 font-normal"> · part {running.progress.replace("/", " of ")}</span>}
            </p>
            <p className="text-sm text-gray-400">A few pages take about ten seconds; a long document can take a few minutes.</p>
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The document could not be translated. Please try again."}</span>
        </div>
      )}

      {detail && detail.status === "completed" && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">
                {detail.detected_language ? `${languageName(detail.detected_language)} → ` : ""}{languageName(detail.language)}
              </p>
              <p className="font-semibold truncate">{detail.original_name}</p>
            </div>
            {detail.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">{detail.engine}</span>}
          </div>

          {stats.paragraphs > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-5 text-center">
              <div className="rounded-xl bg-navy border border-navy-border py-3"><p className="text-xl font-extrabold text-accent-light">{stats.words.toLocaleString()}</p><p className="text-[11px] text-gray-400">words translated</p></div>
              <div className="rounded-xl bg-navy border border-navy-border py-3"><p className="text-xl font-extrabold text-accent-light">{stats.paragraphs.toLocaleString()}</p><p className="text-[11px] text-gray-400">paragraphs</p></div>
              <div className="rounded-xl bg-navy border border-navy-border py-3"><p className="text-xl font-extrabold text-accent-light">{stats.seconds}s</p><p className="text-[11px] text-gray-400">time taken</p></div>
            </div>
          )}

          {extra.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-5">{extra.notice}</p>}

          <div className="flex flex-wrap gap-2 mb-4">
            {[["both", "Side by side"], ["translation", "Translation only"]].map(([key, label]) => (
              <button key={key} type="button" onClick={() => setLayout(key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${layout === key ? "bg-accent-light text-navy" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="rounded-xl bg-navy border border-navy-border divide-y divide-navy-border max-h-[560px] overflow-y-auto">
            {visible.map(([original, translated], i) => (
              <div key={i} className={layout === "both" ? "grid md:grid-cols-2 gap-x-6 gap-y-2 p-4" : "p-4"}>
                {layout === "both" && <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">{original}</p>}
                <p dir={rtl ? "rtl" : "ltr"} className="text-gray-100 leading-relaxed whitespace-pre-line">{translated}</p>
              </div>
            ))}
          </div>
          {pairs.length > visible.length && (
            <button type="button" onClick={() => setShowAll(true)} className="mt-3 text-sm text-accent-light hover:underline">
              Show all {pairs.length.toLocaleString()} paragraphs (the downloaded file always has all of them)
            </button>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            {detail.result_audio && (
              <button type="button" onClick={downloadDocument} disabled={downloading} className="px-5 py-2 rounded-full bg-brand-gradient text-white text-sm font-semibold inline-flex items-center gap-2 shadow-md shadow-accent/20 disabled:opacity-60">
                {downloading ? <FiLoader className="animate-spin" /> : <FiDownload />} Download {fileExtension === ".pptx" ? "PowerPoint" : "Word"} ({fileExtension})
              </button>
            )}
            <CopyButton getText={() => detail.transcript} label="Copy translation" />
            <button type="button" onClick={() => saveBlob(`${baseName}-${detail.language}.txt`, new Blob([detail.transcript], { type: "text/plain;charset=utf-8" }))} className={pillClass}>
              <FiDownload /> Download .txt
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            {detail.engine && detail.engine.startsWith("Claude")
              ? "Translated by an AI model that reads the whole passage, so it follows the meaning. Have important documents (contracts, legal or medical text) checked by a professional translator."
              : "Machine translation is fast and usually accurate, but it can miss context, idioms and legal wording. Have important documents (contracts, legal or medical text) checked by a professional translator."}
          </p>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your translations</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your translations will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border p-4 ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                <p className="text-sm text-gray-200 truncate">{job.original_name || "Document"} → {languageName(job.language)}</p>
                <p className="text-xs text-gray-500 mb-3">{new Date(job.created_at).toLocaleString()} · {job.status === "completed" ? "Ready" : job.status === "failed" ? "Failed" : "Working..."}</p>
                <div className="flex items-center justify-between gap-3">
                  {job.status === "completed" && job.has_transcript ? (
                    <button type="button" onClick={() => setViewId(job.id)} className="text-sm text-accent-light hover:underline">View translation</button>
                  ) : job.status === "failed" ? <p className="text-xs text-red-300">{job.error_message}</p> : <span />}
                  {job.status !== "processing" && job.status !== "queued" && (
                    <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
